import { useState, useEffect, useCallback, useRef } from "react";
import API from "@app/services/api";
import type { PatientSelectV2 } from "@app/types/patient";

const PAGE_SIZE = 20;

/** The dashboard's selection, as the board sends it: its conditions and its period. */
export interface PatientSelection {
  filters?: string;
  periode?: string;
  periodeStart?: string;
  periodeEnd?: string;
}

type PickerResponse = { ok?: boolean; data?: { patients?: PatientSelectV2[]; narrowed?: boolean } };

interface UsePatientSelectorOptions {
  /** Whether to skip fetching (e.g. when on dataset route). */
  skip?: boolean;
  /**
   * Externally controlled visibility. Left undefined, the hook owns it — what
   * the toolbar combobox does. Passed in, the caller owns it, so a surface other
   * than the trigger (a Sankey ribbon) can open the list.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The practitioner the dashboard is narrowed to, so the list never offers a dossier it does not count (PT-111). */
  praticien?: string | null;
  /** The dashboard's selection, so the list offers only the dossiers its counter lists (PT-148). */
  selection?: PatientSelection;
}

interface UsePatientSelectorResult {
  open: boolean;
  setOpen: (v: boolean) => void;
  searchValue: string;
  setSearchValue: (v: string) => void;
  patients: PatientSelectV2[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  /** Whether the server narrowed the list to the selection — not when it narrows nothing. */
  narrowed: boolean;
}

export function usePatientSelector({
  skip = false,
  open: controlledOpen,
  onOpenChange,
  praticien,
  selection,
}: UsePatientSelectorOptions): UsePatientSelectorResult {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  const [searchValue, setSearchValue] = useState("");
  const [patients, setPatients] = useState<PatientSelectV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [narrowed, setNarrowed] = useState(false);

  // Stable ref for offset used by loadMore without re-creating the callback
  const offsetRef = useRef(0);
  // Ref-based loading flag prevents double calls when scroll events fire before
  // the React state update propagates (race condition with rapid scrolling).
  const loadingRef = useRef(false);
  const searchRef = useRef(searchValue);
  searchRef.current = searchValue;
  const praticienRef = useRef(praticien);
  praticienRef.current = praticien;
  // Serialised: callers build the selection on each render. Undefined keys drop out.
  const selectionKey = JSON.stringify(selection ?? {});
  const selectionRef = useRef(selectionKey);
  selectionRef.current = selectionKey;

  // Initial / refresh fetch: runs when the selector opens or the search changes
  useEffect(() => {
    if (!open || skip) return;

    let cancelled = false;
    setPatients([]);
    setHasMore(false);
    offsetRef.current = 0;
    loadingRef.current = false;

    const delay = searchValue.trim() ? 300 : 0;
    const timer = setTimeout(() => {
      loadingRef.current = true;
      setLoading(true);
      const query: Record<string, string> = { ...JSON.parse(selectionKey), offset: "0" };
      if (searchValue.trim()) query.search = searchValue.trim();
      if (praticien) query.praticien = praticien;

      API.get({ path: "/patient/all-for-select", query })
        .then((res: PickerResponse) => {
          if (cancelled) return;
          const rows = res?.ok && Array.isArray(res?.data?.patients) ? res.data.patients : [];
          setPatients(rows);
          setNarrowed(Boolean(res?.data?.narrowed));
          setHasMore(rows.length === PAGE_SIZE);
          offsetRef.current = rows.length;
        })
        .catch(() => {
          if (!cancelled) {
            setPatients([]);
            setHasMore(false);
          }
        })
        .finally(() => {
          if (!cancelled) {
            loadingRef.current = false;
            setLoading(false);
          }
        });
    }, delay);

    return () => { cancelled = true; clearTimeout(timer); loadingRef.current = false; };
  }, [open, skip, searchValue, praticien, selectionKey]);

  const loadMore = useCallback(() => {
    // Use the ref (not stale state) to guard against double calls when scroll
    // events fire faster than React state propagation.
    if (loadingRef.current || !hasMore) return;
    const currentOffset = offsetRef.current;
    loadingRef.current = true;
    setLoading(true);

    const query: Record<string, string> = {
      ...JSON.parse(selectionRef.current),
      offset: currentOffset.toString(),
    };
    if (searchRef.current.trim()) query.search = searchRef.current.trim();
    if (praticienRef.current) query.praticien = praticienRef.current;

    API.get({ path: "/patient/all-for-select", query })
      .then((res: PickerResponse) => {
        const rows = res?.ok && Array.isArray(res?.data?.patients) ? res.data.patients : [];
        setPatients((prev) => [...prev, ...rows]);
        setHasMore(rows.length === PAGE_SIZE);
        offsetRef.current = currentOffset + rows.length;
      })
      .catch(() => setHasMore(false))
      .finally(() => {
        loadingRef.current = false;
        setLoading(false);
      });
  }, [hasMore]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setPatients([]);
      setHasMore(false);
      offsetRef.current = 0;
      loadingRef.current = false;
    }
  }, [open]);

  return { open, setOpen, searchValue, setSearchValue, patients, loading, hasMore, loadMore, narrowed };
}
